import { removeLastEmptyPage } from '@/entities/node/lib/list-node-operation'
import { yCustomNodeDatas, yTaskInfo, ydoc } from '@/shared/config/yjs-config'
import { YJS_TASK_LIFECYCLE_ORIGIN } from '@/shared/lib/yjs/transaction-origin'
import { proxy } from 'valtio'
import { useSnapshot } from 'valtio/react'
import * as Y from 'yjs'

export type TaskRunNodeState = 0 | 1 | -1

export interface GlobalTaskInfo {
  nodeId?: string
  runId?: string
  runMap?: Record<string, TaskRunNodeState>
  isGroup?: boolean
  state?: number
}

interface TaskInfoStoreState {
  taskInfo: GlobalTaskInfo
  runMap: Record<string, TaskRunNodeState> | null
}

function readTaskInfoFromYjs(): GlobalTaskInfo {
  return yTaskInfo.toJSON() as GlobalTaskInfo
}

function syncTaskInfoStore() {
  const taskInfo = readTaskInfoFromYjs()
  taskInfoStore.taskInfo = taskInfo
  taskInfoStore.runMap = taskInfo.runMap ?? null
}

export const taskInfoStore = proxy<TaskInfoStoreState>({
  taskInfo: readTaskInfoFromYjs(),
  runMap: readTaskInfoFromYjs().runMap ?? null,
})

let initialized = false

export function initTaskInfoStoreSync() {
  if (initialized) return

  initialized = true
  syncTaskInfoStore()
  yTaskInfo.observeDeep(syncTaskInfoStore)
}

initTaskInfoStoreSync()

export function useTaskInfoSnapshot() {
  return useSnapshot(taskInfoStore)
}

export function readGlobalTaskRunMap(): Record<string, TaskRunNodeState> | null {
  return taskInfoStore.runMap ? { ...taskInfoStore.runMap } : null
}

export function hasRunningGlobalTask(): boolean {
  return taskInfoStore.taskInfo.state === 0
}

export function isCurrentGroupTask(nodeId: string): boolean {
  const normalizedNodeId = nodeId.trim()
  const taskInfo = taskInfoStore.taskInfo

  return Boolean(
    normalizedNodeId &&
    taskInfo.isGroup === true &&
    taskInfo.nodeId === normalizedNodeId,
  )
}

export function markTaskInfoSubmitted(args: {
  nodeId: string
  runId: string
  isGroup: boolean
  isContinue: boolean
  runMap?: Record<string, TaskRunNodeState>
}) {
  const nodeId = args.nodeId.trim()
  const runId = args.runId.trim()
  if (!nodeId || !runId) return
  if (hasRunningGlobalTask()) return

  const runMap = new Y.Map<TaskRunNodeState>()
  Object.entries(args.runMap ?? {}).forEach(([nodeId, state]) => {
    runMap.set(nodeId, state)
  })

  ydoc.transact(() => {
    if(args.isContinue) {
      yTaskInfo.set('state', 0)
    } else {
      yTaskInfo.set('nodeId', nodeId)
      yTaskInfo.set('runId', runId)
      yTaskInfo.set('runMap', runMap)
      yTaskInfo.set('isGroup', args.isGroup)
      yTaskInfo.set('state', 0)
    }
  }, YJS_TASK_LIFECYCLE_ORIGIN)
}

export async function clearTaskInfoIfCurrentRun() {
  const taskInfo = readTaskInfoFromYjs()
  ydoc.transact(() => {
    if(taskInfo?.state === 0) {
      const runMap = taskInfo.runMap ?? {}
      Object.entries(runMap).forEach(([nodeId, state]) => {
        if(state === 0) {
          // 将运行中的节点状态置为 failed，前端根据状态变化触发相关逻辑
            const yNodeData = yCustomNodeDatas.get(nodeId);
            if (yNodeData) {
              const runId = yNodeData.get('runId')
              if (runId !== taskInfo.runId) return
              yNodeData.set('status', 'failed');
              yNodeData.set('runId', '')
              removeLastEmptyPage(nodeId);
            } else if(nodeId.indexOf('#') != -1) {
              // 列表节点
              clearListTask(nodeId);
            }
        }
      });
    }
    yTaskInfo.clear();
  }, YJS_TASK_LIFECYCLE_ORIGIN)
}

/** 列表节点停止任务 */
export function clearListTask(nodeId: string) {
  ydoc.transact(() => {
    if(nodeId.indexOf('#') != -1) {
      // 列表节点
      const realNodeId = nodeId.split('#')[0];
      const yNodeData = yCustomNodeDatas.get(realNodeId);
      if (yNodeData) {
        yNodeData.set('status', 'completed');
        yNodeData.set('runId', '')
        yNodeData.set('taskId', '')
        yNodeData.delete('createRefs')
        removeLastEmptyPage(realNodeId);
      }
    } else {
      const yNodeData = yCustomNodeDatas.get(nodeId);
      if (yNodeData) {
        yNodeData.set('status', '')
        yNodeData.set('runId', '')
        yNodeData.set('taskId', '')
        removeLastEmptyPage(nodeId);
      }
    }
  }, YJS_TASK_LIFECYCLE_ORIGIN)
}
